import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { batch1 } from './pillarData';
import { batch2 } from './pillarData2';

const allArticles = [...batch1, ...batch2];

export default function PillarArticle({ slug }: { slug: string }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const article = allArticles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <div className="pt-32 pb-20 text-center px-4">
          <p className="text-foreground-600">{t('not_found.title')}</p>
          <button
            onClick={() => navigate('/')}
            className="mt-6 px-6 py-3 bg-primary-500 text-white font-semibold text-sm rounded-md hover:bg-primary-600 transition-colors"
          >
            {t('not_found.back_home')}
          </button>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      <article className="pt-24 md:pt-28 pb-16 md:pb-24">
        <div className="w-full px-4 md:px-8 lg:px-12 max-w-4xl mx-auto">
          <nav className="mb-6 text-sm text-foreground-500" aria-label="Breadcrumb">
            <button
              onClick={() => navigate('/')}
              className="hover:text-primary-600 transition-colors"
            >
              {t('nav.home')}
            </button>
            <span className="mx-2">/</span>
            <span className="text-foreground-700">{article.title}</span>
          </nav>

          <header className="mb-10 md:mb-14">
            <h1 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-900 mb-4 leading-tight">
              {article.title}
            </h1>
            <p className="text-sm text-foreground-500">
              Published: {article.published} · Updated: {article.published} · Reading time: {article.readingTime}
            </p>
          </header>

          <div className="prose prose-sm md:prose-base max-w-none text-foreground-700 leading-relaxed space-y-6 md:space-y-8">
            <img
              src={article.image}
              alt={article.title}
              className="w-full h-56 md:h-80 object-cover rounded-lg mb-8"
            />

            <p className="text-base md:text-lg text-foreground-700">{article.intro}</p>

            {article.sections.map((section) => (
              <section key={section.heading}>
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mt-8 mb-4">
                  {section.heading}
                </h2>
                {section.paragraphs.map((para, i) => (
                  <p key={i} className="mb-4">
                    {para}
                  </p>
                ))}
                {section.list && (
                  <div className="bg-background-100 rounded-lg p-5 md:p-6 border border-background-200/70">
                    <ul className="space-y-2 text-sm">
                      {section.list.map((item, i) => (
                        <li key={i} className="flex items-start gap-2">
                          <i className="ri-checkbox-circle-line text-primary-500 mt-0.5 flex-shrink-0" />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </section>
            ))}

            {article.faq.length > 0 && (
              <section>
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mt-8 mb-4">
                  Frequently Asked Questions
                </h2>
                <div className="space-y-4">
                  {article.faq.map((item) => (
                    <div key={item.q}>
                      <h3 className="font-semibold text-foreground-900 mb-1">{item.q}</h3>
                      <p className="text-sm">{item.a}</p>
                    </div>
                  ))}
                </div>
              </section>
            )}

            <div className="bg-primary-50 rounded-lg p-5 md:p-6 border border-primary-200/70 mt-10">
              <p className="text-sm text-foreground-700 mb-4">
                <strong>Disclaimer:</strong> This website is an independent informational resource. Information can change and should be independently verified before making purchasing decisions. We are not affiliated with AllChinaBuy or any sellers mentioned. Always verify current prices, shipping rates, and policies directly on the platform.
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-primary-500 text-white font-semibold text-sm rounded-md hover:bg-primary-600 transition-colors whitespace-nowrap"
                >
                  Explore AllChinaBuy Spreadsheet
                  <i className="ri-arrow-right-line" />
                </a>
                <button
                  onClick={() => navigate('/')}
                  className="inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-white text-foreground-700 font-semibold text-sm rounded-md border border-background-200 hover:bg-background-100 transition-colors whitespace-nowrap"
                >
                  <i className="ri-home-line" />
                  Back to Home
                </button>
              </div>
            </div>
          </div>
        </div>
      </article>

      <Footer />
    </div>
  );
}